import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { viewTransition } from '@theme/utilities';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { StandardEvents } from '@shopify/events';

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLElement[]} [cards] - The cards elements.
 * @property {HTMLElement} [loadingStatus] - Visible and announced automatic-loading status.
 * @property {HTMLElement} [loadMoreContainer] - Hybrid pagination controls.
 * @property {HTMLAnchorElement} [loadMoreLink] - Progressively enhanced next-page link.
 * @property {HTMLElement} [loadMoreLabel] - Visible load-more link label.
 * @property {HTMLElement} [loadMoreError] - Retryable pagination error.
 * @property {HTMLElement} [loadedItemCount] - Number of product cards currently rendered.
 *
 * @typedef {import('@shopify/events').CollectionUpdateEvent | import('@shopify/events').SearchUpdateEvent} FilterUpdateEvent
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /** @type {number} */
  #automaticallyLoadedPages = 0;

  /** @type {boolean} */
  #isLoadingNextPage = false;

  /** @type {number} */
  #paginationVersion = 0;

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard,
      });
    }

    this.#initializePagination();

    // Listen for filter updates to clear cached pages
    document.addEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.addEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listeners
    document.removeEventListener(StandardEvents.searchUpdate, this.#handleFilterUpdate);
    document.removeEventListener(StandardEvents.collectionUpdate, this.#handleFilterUpdate);
  }

  #initializePagination() {
    this.infinityScrollObserver?.disconnect();
    this.#automaticallyLoadedPages = 0;
    this.#isLoadingNextPage = false;
    this.#setLoadMoreLoadingState(false);
    this.#updateLoadedItemCount();
    this.#clearLoadMoreError();
    this.#updateLoadMoreControl();
    this.#observeViewMore();
  }

  #observeViewMore() {
    const { viewMoreNext } = this.refs;

    if (!viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              if (entry.target === this.refs.viewMoreNext) {
                if (!this.#shouldAutomaticallyLoadNextPage()) {
                  this.#updateLoadMoreControl(true);
                  continue;
                }

                try {
                  const appended = await this.#loadNextPage(true);
                  if (appended) this.#automaticallyLoadedPages += 1;
                  this.#updateLoadMoreControl();
                } catch {
                  this.#showLoadMoreError();
                  this.#updateLoadMoreControl(true);
                }
              }
            }
          }
        },
        {
          rootMargin: '600px',
        }
      );
    }

    if (this.#shouldAutomaticallyLoadNextPage()) {
      this.infinityScrollObserver.observe(viewMoreNext);
    } else {
      this.#updateLoadMoreControl(true);
    }
  }

  #shouldAutomaticallyLoadNextPage() {
    if (!this.hasAttribute('auto-load-pages')) return true;

    return this.#automaticallyLoadedPages < this.#automaticPageLimit;
  }

  get #automaticPageLimit() {
    return Math.max(0, Number(this.getAttribute('auto-load-pages')) || 0);
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  async #fetchNextPage() {
    const page = this.#getNextPage();

    if (!page || !this.#shouldUsePage(page)) {
      return false;
    }

    return this.#fetchSpecificPage(page.page, page.url);
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const paginationVersion = this.#paginationVersion;
    const requestUrl = url ? new URL(url.toString()) : new URL(window.location.href);

    if (!url) {
      requestUrl.searchParams.set('page', pageNumber.toString());
      requestUrl.hash = '';
    }

    const pageInfo = { page: pageNumber, url: requestUrl };
    if (!this.#shouldUsePage(pageInfo)) return false;
    const pageContent = await sectionRenderer.getSectionHTML(this.sectionId, false, requestUrl);
    if (paginationVersion !== this.#paginationVersion) return false;

    const parsedPage = new DOMParser().parseFromString(pageContent, 'text/html');
    if (!parsedPage.querySelector('[ref="grid"]')) {
      throw new Error(`Product results page ${pageNumber} did not contain a grid`);
    }

    this.pages.set(pageNumber, pageContent);
    return true;
  }

  /**
   * @param {boolean} showLoadingStatus - Whether to show the passive automatic-loading status.
   */
  async #loadNextPage(showLoadingStatus = false) {
    if (this.#isLoadingNextPage) return false;

    this.#isLoadingNextPage = true;
    this.#setLoadMoreLoadingState(true, showLoadingStatus);

    try {
      return await this.#renderNextPage();
    } finally {
      this.#isLoadingNextPage = false;
      this.#setLoadMoreLoadingState(false);
    }
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return false;

    const nextPage = this.#getNextPage();

    if (!nextPage || !this.#shouldUsePage(nextPage)) return false;
    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const fetched = await this.#fetchNextPage();
      if (!fetched) return false;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
      if (!nextPageItemElements) throw new Error(`Unable to load product results page ${nextPage.page}`);
    }

    grid.append(...nextPageItemElements);

    this.#aspectRatioHelper?.processNewElements();
    this.#updateLoadedItemCount();

    return true;
  }

  /**
   * Progressively enhances the server-rendered next-page link.
   * @param {Event} event
   */
  async loadMore(event) {
    event.preventDefault();
    this.#clearLoadMoreError();

    try {
      await this.#loadNextPage();
      this.#updateLoadMoreControl(true);
    } catch {
      this.#showLoadMoreError();
      this.#updateLoadMoreControl(true);
    }
  }

  #updateLoadedItemCount() {
    const { grid, loadedItemCount } = this.refs;
    if (!grid || !loadedItemCount) return;

    loadedItemCount.textContent = String(grid.querySelectorAll(':scope > [ref="cards[]"]').length);
  }

  #updateLoadMoreControl(forceVisible = false) {
    const { loadMoreContainer, loadMoreLink, viewMoreNext } = this.refs;
    if (!loadMoreContainer || !loadMoreLink) return;

    const nextPage = this.#getNextPage();
    const hasNextPage = this.#shouldUsePage(nextPage);
    const shouldShow = hasNextPage && (forceVisible || !this.#shouldAutomaticallyLoadNextPage());

    loadMoreContainer.hidden = !shouldShow;

    if (hasNextPage && nextPage) {
      loadMoreLink.href = nextPage.url.toString();
    }

    if (shouldShow && viewMoreNext) {
      this.infinityScrollObserver?.unobserve(viewMoreNext);
    }
  }

  /**
   * @param {boolean} isLoading
   * @param {boolean} showLoadingStatus
   */
  #setLoadMoreLoadingState(isLoading, showLoadingStatus = false) {
    const { grid, loadingStatus, loadMoreContainer, loadMoreLink, loadMoreLabel } = this.refs;

    if (grid) grid.setAttribute('aria-busy', String(isLoading));
    if (loadingStatus) loadingStatus.hidden = !(isLoading && showLoadingStatus);

    if (!loadMoreLink || !loadMoreLabel) return;

    const label = isLoading ? loadMoreContainer?.dataset.loadingLabel : loadMoreContainer?.dataset.loadMoreLabel;
    if (label) loadMoreLabel.textContent = label;

    loadMoreLink.setAttribute('aria-disabled', String(isLoading));
    loadMoreLink.toggleAttribute('data-loading', isLoading);
  }

  #showLoadMoreError() {
    if (this.refs.loadMoreError) this.refs.loadMoreError.hidden = false;
  }

  #clearLoadMoreError() {
    if (this.refs.loadMoreError) this.refs.loadMoreError.hidden = true;
  }

  /**
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getNextPage() {
    const { cards } = this.refs;

    if (!Array.isArray(cards)) return;

    const targetCard = cards[cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    url.hash = '';

    return {
      page,
      url,
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages.
   * Only reacts to events from the same section (ignores e.g. predictive search in the header).
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const eventSection = /** @type {Element | null} */ (event.target)?.closest('[id^="shopify-section-"]');
    if (eventSection && eventSection.id !== `shopify-section-${this.sectionId}`) return;
    this.#paginationVersion += 1;
    this.pages.clear();

    const updatePromise = event.promise;
    if (!(updatePromise instanceof Promise)) return;

    updatePromise
      .then(() => {
        if (this.isConnected) this.#initializePagination();
      })
      .catch(() => {
        // The existing results remain usable when a filter request fails.
      });
  };
}
